// Shared bill-version XML parsing, extracted from BillProfile.js so the bill profile page and
// the version comparison page render tracked-changes (insertion/deletion) markup identically.

export function formatActionText(text) {
    if (!text) return '';
    // Replace underscores with spaces and Title Case
    return text.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

export function formatDate(dateStr) {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function applyMarkStyling(html) {
    if (!html) return '';

    let processedHtml = html;

    // Handle EnSpace
    processedHtml = processedHtml.replace(/<span class="EnSpace"(\s*\/|><\/span)>/g, ' ');

    // 1. Handle Deletions: These are self-closing tags with content in the 'data' attribute.
    processedHtml = processedHtml.replace(/<\?xm-deletion_mark data="([\s\S]+?)"\?>/g, (match, dataContent) => {
        const tempDiv = document.createElement('div');
        const unescapedData = dataContent.replace(/\\"/g, '"');
        tempDiv.innerHTML = unescapedData;
        const textOnly = tempDiv.textContent || tempDiv.innerText || '';
        return `<span class="bill-deletion">${textOnly}</span>`;
    });

    // 2. Handle Insertions: These have start and end tags wrapping the content.
    // This now handles inline insertions. Block-level insertions are handled by pre-processing in `processXMLContent`.
    processedHtml = processedHtml.replace(/<\?xm-insertion_mark_start\s*\?>([\s\S]*?)<\?xm-insertion_mark_end\s*\?>/g, '<span class="bill-insertion">$1</span>');

    processedHtml = processedHtml.replace(/<\?xm-insertion_mark_start \?>([\s\S]*?)<\?xm-insertion_mark_end \?>/g, (match, insertionContent) => {
        return `<span class="bill-insertion">${insertionContent}</span>`;
    });

    // 3. Final cleanup of any leftover XML-namespaced tags.
    processedHtml = processedHtml.replace(/<\/?(xhtml|shtml):[^>]*>/g, '');
    processedHtml = processedHtml.replace(/ xmlns="http:\/\/www.w3.org\/1999\/xhtml"/g, '');

    // 4. Strip hardcoded width/height attributes from table elements so CSS can control sizing.
    processedHtml = processedHtml.replace(/(<(?:table|td|th|col|colgroup)\b[^>]*?)\s+width="[^"]*"/gi, '$1');
    processedHtml = processedHtml.replace(/(<(?:table|td|th|col|colgroup)\b[^>]*?)\s+height="[^"]*"/gi, '$1');

    return processedHtml;
}

export function processXMLContent(xmlContent) {
    try {
        // Pre-process to make inserted sections parsable by replacing PI tags with divs
        // 1. Deletions: Decode the data attribute and wrap in a deletion div
        let parsableXml = xmlContent
            .replace(/<\?xm-deletion_mark\s+data="([\s\S]*?)"\s*\?>/g, (match, data) => {
                let decoded = data
                    .replace(/&lt;/g, '<')
                    .replace(/&gt;/g, '>')
                    .replace(/&quot;/g, '"')
                    .replace(/&apos;/g, "'")
                    .replace(/&amp;/g, '&');
                decoded = decoded.replace(/\\"/g, '"');
                return `<span class="bill-deletion">${decoded}</span>`;
            })
            .replace(/<\?xm-insertion_mark_start\s*\?>/g, '<span class="bill-insertion">')
            .replace(/<\?xm-insertion_mark_end\s*\?>/g, '</span>');

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(parsableXml, "text/xml");

        // Helper to safely get styled text content
        const getStyledText = (tag) => {
            const el = xmlDoc.getElementsByTagName('caml:' + tag)[0] || xmlDoc.getElementsByTagName(tag)[0];
            if (!el) return '';
            const serializer = new XMLSerializer();
            const inner = Array.from(el.childNodes).map(child => serializer.serializeToString(child)).join('');
            return applyMarkStyling(inner.trim());
        };

        // 1. History
        const actions = [];
        const historyNode = xmlDoc.getElementsByTagName('caml:History')[0] || xmlDoc.getElementsByTagName('History')[0];

        if (historyNode) {
            const actionNodes = historyNode.getElementsByTagName('caml:Action').length ? historyNode.getElementsByTagName('caml:Action') : historyNode.getElementsByTagName('Action');
            for (let i = 0; i < actionNodes.length; i++) {
                const text = actionNodes[i].getElementsByTagName('caml:ActionText')[0]?.textContent || actionNodes[i].getElementsByTagName('ActionText')[0]?.textContent;
                const date = actionNodes[i].getElementsByTagName('caml:ActionDate')[0]?.textContent || actionNodes[i].getElementsByTagName('ActionDate')[0]?.textContent;
                if (text && date) actions.push({ text, date });
            }
        }

        // 2. Author & Intro Date
        const authorNodes = xmlDoc.getElementsByTagName('caml:AuthorText').length
            ? xmlDoc.getElementsByTagName('caml:AuthorText')
            : xmlDoc.getElementsByTagName('AuthorText');

        let authorText = '';
        if (authorNodes.length > 0) {
            let startNode = authorNodes[0];
            let parent = startNode.parentNode;

            // Handle case where AuthorText is wrapped in an insertion/deletion span
            if (parent.nodeName.toLowerCase() === 'span' && (parent.getAttribute('class') === 'bill-insertion' || parent.getAttribute('class') === 'bill-deletion')) {
                startNode = parent;
                parent = parent.parentNode;
            }

            const childNodes = Array.from(parent.childNodes);

            const firstAuthorIndex = childNodes.indexOf(startNode);
            const digestNode = xmlDoc.getElementsByTagName('caml:DigestText')[0] || xmlDoc.getElementsByTagName('DigestText')[0];
            const digestIndex = digestNode ? childNodes.indexOf(digestNode) : -1;

            const authorBlockNodes = childNodes.slice(firstAuthorIndex, digestIndex > -1 ? digestIndex : childNodes.length);

            const rawAuthorHtml = authorBlockNodes
                .filter(node => {
                    const isAuthorText = node.nodeType === 1 && node.tagName.includes('AuthorText');
                    const isInsertionDeletion = node.nodeType === 1 && node.nodeName.toLowerCase() === 'span' &&
                        (node.getAttribute('class') === 'bill-insertion' || node.getAttribute('class') === 'bill-deletion');
                    return node.nodeType === 7 || isAuthorText || isInsertionDeletion;
                })
                .map(node => new XMLSerializer().serializeToString(node))
                .join('<br/>');

            authorText = applyMarkStyling(rawAuthorHtml);
        }
        const introDate = actions.length > 0 ? actions[0].date : '';

        // 3. Title
        const titleText = getStyledText('Title') || getStyledText('caml:Title');

        // 4. Digest Line
        const measureType = getStyledText('MeasureType') || getStyledText('caml:MeasureType');
        const measureNum = getStyledText('MeasureNum') || getStyledText('caml:MeasureNum');
        const digestAuthor = getStyledText('Name') || getStyledText('caml:Name');
        const subject = getStyledText('Subject') || getStyledText('caml:Subject');
        const isAmended = actions.some(a => (a.text || '').toUpperCase().includes('AMENDED'));
        const statusText = isAmended ? 'as amended' : 'as introduced';
        const digestLine = `${measureType} ${measureNum}, ${statusText}, ${digestAuthor}. ${subject}`;

        // 5. Digest Text
        const digestNode = xmlDoc.getElementsByTagName('caml:DigestText')[0] || xmlDoc.getElementsByTagName('DigestText')[0];
        let digestHtml = digestNode ? new XMLSerializer().serializeToString(digestNode) : '';
        // Strip outer tag
        digestHtml = digestHtml.replace(/^<[^>]+>|<\/[^>]+>$/g, '');
        digestHtml = applyMarkStyling(digestHtml);

        // 6. Digest Key
        const keys = {
            vote: getStyledText('VoteRequired') || getStyledText('caml:VoteRequired'),
            approp: getStyledText('Appropriation') || getStyledText('caml:Appropriation'),
            fiscal: getStyledText('FiscalCommittee') || getStyledText('caml:FiscalCommittee'),
            local: getStyledText('LocalProgram') || getStyledText('caml:LocalProgram'),
            urgency: getStyledText('Urgency') || getStyledText('caml:Urgency'),
            tax: getStyledText('TaxLevy') || getStyledText('caml:TaxLevy')
        };

        // 7. Preamble
        const preamble = getStyledText('Preamble') || getStyledText('caml:Preamble');

        // 8. Bill Content Rendering
        const billNode = xmlDoc.querySelector('Bill, caml\\:Bill, Resolution, caml\\:Resolution');
        let contentHtml = "";

        if (billNode) {
            const renderNode = (node) => {
                if (node.nodeType === 3) return applyMarkStyling(node.textContent); // Text nodes
                if (node.nodeType !== 1) return ""; // Other nodes (comments, etc.)

                const tagName = node.tagName.replace(/caml:/i, '');
                let html = "";

                // Helper to get the full inner markup of a node and apply styling.
                const getStyledInnerHtml = (el) => {
                    if (!el) return "";
                    const serializer = new XMLSerializer();
                    const inner = Array.from(el.childNodes).map(child => serializer.serializeToString(child)).join('');
                    return applyMarkStyling(inner);
                };

                switch (tagName) {
                    case 'Preamble':
                        return `<div class="mb-3 text-uppercase">${getStyledInnerHtml(node)}</div>`;

                    case 'BillSection': {
                        const numHtml = getStyledInnerHtml(node.querySelector(':scope > Num, :scope > caml\\:Num'));
                        const actionLineHtml = getStyledInnerHtml(node.querySelector(':scope > ActionLine, :scope > caml\\:ActionLine'));

                        // Iterate over children to handle potential insertion/deletion wrappers
                        const bodyNodes = Array.from(node.childNodes).filter(child => {
                            if (child.nodeType !== 1) return true; // Keep text nodes
                            const tagName = child.tagName.replace(/caml:/i, '');
                            return !['Num', 'ActionLine'].includes(tagName);
                        });
                        let childrenHtml = bodyNodes.map(renderNode).join('');

                        html += `<div class="bill-section mt-4">`;
                        if (actionLineHtml) {
                            html += `<p><strong>${numHtml}</strong> ${actionLineHtml}</p>`;
                            html += childrenHtml;
                        } else {
                            if (childrenHtml.trim().startsWith('<p>')) {
                                childrenHtml = childrenHtml.replace('<p>', `<p><strong>${numHtml}</strong> `);
                            } else {
                                childrenHtml = `<p><strong>${numHtml}</strong></p>${childrenHtml}`;
                            }
                            html += childrenHtml;
                        }
                        html += `</div>`;
                        break;
                    }

                    case 'Fragment': {
                        Array.from(node.children).forEach(child => {
                            html += renderNode(child);
                        });
                        break;
                    }

                    case 'LawHeading': {
                        const hNumHtml = getStyledInnerHtml(node.querySelector(':scope > Num, :scope > caml\\:Num'));
                        const version = node.querySelector('LawHeadingVersion, caml\\:LawHeadingVersion') || node;
                        const hTextHtml = getStyledInnerHtml(version.querySelector('LawHeadingText, caml\\:LawHeadingText'));

                        html += `<div class="text-center mt-3 mb-2 text-uppercase"><strong>DIVISION ${hNumHtml} ${hTextHtml}</strong></div>`;

                        // Recurse on children that are not part of the heading's own text
                        Array.from(node.children).forEach(child => {
                            const childTag = child.tagName.replace(/caml:/i, '');
                            if (!['Num', 'LawHeadingVersion'].includes(childTag)) {
                                html += renderNode(child);
                            }
                        });
                        break;
                    }

                    case 'LawSection': {
                        const lNumHtml = getStyledInnerHtml(node.querySelector(':scope > Num, :scope > caml\\:Num'));
                        const version = node.querySelector('LawSectionVersion, caml\\:LawSectionVersion') || node;
                        const lContentHtml = getStyledInnerHtml(version.querySelector('Content, caml\\:Content'));

                        html += `<div class="law-section mt-3">`;
                        if (lContentHtml.trim().startsWith('<p>')) {
                            html += lContentHtml.replace('<p>', `<p><strong>${lNumHtml}</strong> `);
                        } else {
                            html += `<p><strong>${lNumHtml}</strong></p>${lContentHtml}`;
                        }
                        html += `</div>`;
                        break;
                    }

                    case 'Content': {
                        // This handles generic content blocks that aren't caught by the parent's getChildHtml
                        html += applyMarkStyling(Array.from(node.childNodes)
                            .map(child => new XMLSerializer().serializeToString(child))
                            .join(''));
                        break;
                    }

                    case 'span': // Handles our <div className="bill-insertion-wrapper">
                        if (node.classList.contains('bill-insertion') || node.classList.contains('bill-deletion')) {
                            const className = node.getAttribute('class');
                            html += `<div class="${className}">`;
                            html += Array.from(node.childNodes).map(renderNode).join('');
                            html += `</div>`;
                        }
                        break;

                    default:
                        // For any unhandled tags, just render their children to be safe.
                        html = Array.from(node.childNodes).map(renderNode).join('');
                        break;
                }
                return html;
            };

            contentHtml = Array.from(billNode.childNodes).map(renderNode).join('');
        }

        return { actions, authorText, introDate, titleText, digestLine, digestHtml, keys, preamble, contentHtml };

    } catch (e) {
        console.error("Error parsing bill XML", e);
        return null;
    }
}
